import { parseAccountIdsFromString } from '@/helpers/parseAccountIdsFromString';
import { createAccountIdsValidator } from '@/helpers/validateAccountIds';
import { useRunTaskMutation } from '@/services/adminTasks';
import { Box, Button, FormikRadioGroup, FormikTextareaInput, toast } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { object, Schema, string } from 'yup';

import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';

import { BulkModifyAccountsResults, BulkModifyAccountsResultsData } from './BulkModifyAccountsResults';

const ACTION_OPTIONS = [
  { label: 'Move to Untrancheable Asset Team', value: 'move' },
  { label: 'Remove from Untrancheable Asset Team', value: 'remove' },
];

interface BulkMoveToStrandedAssetTeamFormData {
  action: string;
  accountIds: string;
}

const schema = {
  action: string().required('Please select an action'),
  accountIds: string()
    .required('Account IDs are required')
    .test('is-valid-mongo-ids', 'Invalid account IDs', createAccountIdsValidator()),
};

export const BulkMoveToStrandedAssetTeamFormSchema: Schema<BulkMoveToStrandedAssetTeamFormData> = object()
  .shape(schema)
  .noUnknown(true);

export function BulkMoveToStrandedAssetTeam() {
  const [results, setResults] = useState<BulkModifyAccountsResultsData>();
  const [runTask] = useRunTaskMutation();

  const handleSubmit = async (
    values: BulkMoveToStrandedAssetTeamFormData,
    { setSubmitting }: FormikHelpers<BulkMoveToStrandedAssetTeamFormData>,
  ) => {
    const loadingToastId = toast.loading('Updating untrancheable asset team status...');
    try {
      let accountIds = await parseAccountIdsFromString(values.accountIds);
      accountIds = accountIds.filter((id: string) => id.trim().length > 0);
      const ownedByStrandedAssetTeam = values.action === 'move';

      const taskResults = await runTask({
        task: 'moveToStrandedAssetTeam',
        accountIds,
        ownedByStrandedAssetTeam,
      }).unwrap();

      setResults(taskResults);
    } catch (e) {
      console.error(e);
      toast.error('Error updating untrancheable asset team status');
    } finally {
      toast.dismiss(loadingToastId);
      setSubmitting(false);
    }
  };

  const initialValues: BulkMoveToStrandedAssetTeamFormData = {
    action: '',
    accountIds: '',
  };

  const handleCloseResults = () => {
    setResults(undefined);
  };

  return (
    <>
      <Helmet>
        <title>Bulk Assign/Unassign to Untrancheable Asset Team</title>
      </Helmet>
      <MainContainer>
        {!results ? (
          <Formik
            validationSchema={BulkMoveToStrandedAssetTeamFormSchema}
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, submitForm }) => (
              <Form>
                <Box>
                  <Box>
                    <FormSection title="Action" description="Select action.">
                      <Field
                        name="action"
                        id="action"
                        options={ACTION_OPTIONS}
                        component={FormikRadioGroup}
                        isDisabled={isSubmitting}
                      />
                    </FormSection>
                    <FormSection title="Account IDs" description="One account ID per line, or comma separated">
                      <Field
                        name="accountIds"
                        id="accountIds"
                        component={FormikTextareaInput}
                        isDisabled={isSubmitting}
                        rows={10}
                      />
                    </FormSection>
                  </Box>
                </Box>
                <Box
                  direction={{
                    base: 'column',
                    tablet: 'row',
                  }}
                  alignItems={{
                    base: 'stretch',
                    tablet: 'flex-end',
                  }}
                  justifyContent={{
                    tablet: 'flex-end',
                  }}
                  childGap="sm"
                  style={{ flexShrink: 0 }}
                  padding="lg 0 0 0"
                >
                  <Button size="md" variant="primary" type="submit" onClick={submitForm} isLoading={isSubmitting}>
                    Submit
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        ) : (
          <BulkModifyAccountsResults results={results} closeResults={handleCloseResults} />
        )}
      </MainContainer>
    </>
  );
}
